import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import { useMailWebSocket } from '../hooks/useMailWebSocket';
import useFirmInfo from '../hooks/useFirmInfo';

const PresenceContext = createContext(null);

// Status enum matching backend
export const UserStatus = {
  AVAILABLE: 'available',
  BUSY: 'busy',
  AWAY: 'away',
  DO_NOT_DISTURB: 'do_not_disturb',
  OFFLINE: 'offline',
  IDLE: 'idle',
  IN_MEETING: 'in_meeting'
};

// Request browser notification permission
const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    try {
      await Notification.requestPermission();
    } catch (e) {
      console.log('Could not request notification permission');
    }
  }
};

// Show browser desktop notification
const showDesktopNotification = (title, body, onClick) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const notification = new Notification(title, {
        body: body,
        icon: '/favicon.ico',
        tag: 'virgo-message', // Prevents duplicate notifications
        requireInteraction: false
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        if (onClick) onClick();
      };

      // Auto-close after 5 seconds
      setTimeout(() => notification.close(), 5000);
    } catch (e) {
      console.log('Could not show desktop notification');
    }
  }
};

// Notification sound (subtle chime)
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Subtle bell-like sound
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(600, audioContext.currentTime + 0.1);
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    // Audio not supported or blocked
    console.log('Could not play notification sound');
  }
};

export const PresenceProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [myPresence, setMyPresence] = useState(null);
  const myPresenceRef = useRef(null);
  const { firmName } = useFirmInfo();
  const [usersPresence, setUsersPresence] = useState({});
  const [unreadSummary, setUnreadSummary] = useState({ total_unread: 0, channels: [] });
  const [emailUnread, setEmailUnread] = useState({ unread_count: 0, urgent_count: 0 });
  const [typingUsers, setTypingUsers] = useState({}); // { channelId: [userId, ...] }
  const [connected, setConnected] = useState(false);
  const [pendingNotifications, setPendingNotifications] = useState([]);

  const eventSourceRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const typingTimeoutsRef = useRef({});

  // Update document title with unread count (Sigil + Email)
  useEffect(() => {
    const baseTitle = firmName || 'Client Portal';
    const totalUnread = (unreadSummary.total_unread || 0) + (emailUnread.unread_count || 0);
    if (totalUnread > 0) {
      document.title = `(${totalUnread}) ${baseTitle}`;
    } else {
      document.title = baseTitle;
    }
  }, [unreadSummary.total_unread, emailUnread.unread_count, firmName]);

  // Load initial presence
  // Keep ref in sync
  useEffect(() => { myPresenceRef.current = myPresence; }, [myPresence]);

  const loadPresence = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await api.get('/presence/me');
      setMyPresence(response.data);
    } catch (error) {
      console.error('Error loading presence:', error);
    }
  }, [isAuthenticated]);

  // Load unread summary (Sigil)
  const loadUnreadSummary = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await api.get('/presence/unread');
      setUnreadSummary(response.data);
    } catch (error) {
      console.error('Error loading unread summary:', error);
    }
  }, [isAuthenticated]);

  // Load email unread count (used only as manual refresh fallback)
  const loadEmailUnread = useCallback(async () => {
    // Email unread counts are delivered via WebSocket (onEmailCount/onEmailStatus).
    // No HTTP polling needed.
  }, []);

  // Receive email status updates via WebSocket (replaces polling)
  useMailWebSocket({
    onEmailStatus: useCallback((data) => {
      setEmailUnread({
        unread_count: data.unread_count || 0,
        urgent_count: data.urgent_count || 0
      });
    }, []),
    onEmailCount: useCallback((count) => {
      setEmailUnread(prev => ({ ...prev, unread_count: count }));
    }, []),
    enabled: isAuthenticated
  });

  // Poll for pending notifications
  const pollNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await api.get('/presence/notifications');
      const notifications = response.data.notifications || [];

      if (notifications.length > 0) {
        // Play sound if enabled
        if (myPresence?.notification_sound_enabled !== false) {
          playNotificationSound();
        }

        // Add to pending notifications for toast display
        notifications.forEach(notification => {
          if (notification.event_type === 'new_message') {
            setPendingNotifications(prev => [...prev, {
              type: 'message',
              channelId: notification.channel_id,
              senderName: notification.sender_name,
              preview: notification.message_preview,
              timestamp: notification.timestamp
            }]);

            // Show desktop notification if enabled and page is not focused
            if (myPresence?.desktop_notifications_enabled !== false && document.hidden) {
              const title = notification.sender_name || 'New Message';
              const body = myPresence?.show_message_preview !== false
                ? (notification.message_preview || 'You have a new message')
                : 'You have a new message';
              showDesktopNotification(title, body);
            }
          }
        });

        // Refresh unread count
        loadUnreadSummary();
      }
    } catch (error) {
      // Silently fail - polling is not critical
    }
  }, [isAuthenticated, myPresence, loadUnreadSummary]);

  // Update my status
  const updateStatus = useCallback(async (status, customText = null) => {
    if (!isAuthenticated) return;

    try {
      const payload = { status };
      if (customText !== null) {
        payload.custom_status_text = customText;
      }
      const response = await api.patch('/presence/me', payload);
      setMyPresence(response.data);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  }, [isAuthenticated]);

  // Update presence settings
  const updateSettings = useCallback(async (settings) => {
    if (!isAuthenticated) return;

    try {
      const response = await api.patch('/presence/me/settings', settings);
      setMyPresence(response.data);
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  }, [isAuthenticated]);

  // Send heartbeat
  const sendHeartbeat = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      await api.post('/presence/heartbeat');
    } catch (error) {
      // Silently fail - heartbeat is not critical
    }
  }, [isAuthenticated]);

  // Send typing indicator
  const sendTypingIndicator = useCallback(async (channelId, isTyping = true) => {
    if (!isAuthenticated) return;

    try {
      await api.post(`/presence/typing/${channelId}?is_typing=${isTyping}`);
    } catch (error) {
      // Silently fail
    }
  }, [isAuthenticated]);

  // Handle SSE events
  const handleSSEEvent = useCallback((event) => {
    const data = JSON.parse(event.data);

    switch (event.type) {
      case 'connected':
        setConnected(true);
        break;

      case 'new_message':
        // Play sound if enabled
        if (myPresence?.notification_sound_enabled !== false) {
          playNotificationSound();
        }
        // Update unread count
        loadUnreadSummary();
        // Add to pending notifications for toast display
        setPendingNotifications(prev => [...prev, {
          type: 'message',
          channelId: data.channel_id,
          senderName: data.sender_name,
          preview: data.message_preview,
          timestamp: data.timestamp
        }]);
        // Show desktop notification if enabled and page is not focused
        if (myPresence?.desktop_notifications_enabled !== false && document.hidden) {
          const title = data.sender_name || 'New Message';
          const body = myPresence?.show_message_preview !== false
            ? (data.message_preview || 'You have a new message')
            : 'You have a new message';
          showDesktopNotification(title, body);
        }
        break;

      case 'typing':
        setTypingUsers(prev => {
          const channelTyping = prev[data.channel_id] || [];
          if (data.is_typing) {
            if (!channelTyping.includes(data.user_id)) {
              // Clear existing timeout for this user
              const timeoutKey = `${data.channel_id}-${data.user_id}`;
              if (typingTimeoutsRef.current[timeoutKey]) {
                clearTimeout(typingTimeoutsRef.current[timeoutKey]);
              }
              // Auto-remove after 3 seconds
              typingTimeoutsRef.current[timeoutKey] = setTimeout(() => {
                setTypingUsers(p => ({
                  ...p,
                  [data.channel_id]: (p[data.channel_id] || []).filter(id => id !== data.user_id)
                }));
              }, 3000);

              return { ...prev, [data.channel_id]: [...channelTyping, data.user_id] };
            }
          } else {
            return { ...prev, [data.channel_id]: channelTyping.filter(id => id !== data.user_id) };
          }
          return prev;
        });
        break;

      case 'status_change':
        setUsersPresence(prev => ({
          ...prev,
          [data.user_id]: { ...prev[data.user_id], status: data.new_status }
        }));
        break;

      case 'heartbeat':
        // Keep-alive, do nothing
        break;

      default:
        console.log('Unknown SSE event:', event.type, data);
    }
  }, [myPresence, loadUnreadSummary]);

  // Setup SSE connection
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    // Initial load (email status comes via WebSocket on connect)
    loadPresence();
    loadUnreadSummary();

    // Request notification permission if desktop notifications are enabled
    requestNotificationPermission();

    // SSE removed — can't pass auth headers, was causing reconnection storms.
    // Polling + WebSocket handle everything.

    // Poll for notifications every 5 seconds (real-time-ish)
    const notificationPollInterval = setInterval(pollNotifications, 30000);

    // Poll for Sigil unread every 30 seconds (email is WS-driven)
    const pollInterval = setInterval(loadUnreadSummary, 30000);

    // Send heartbeat every 60 seconds
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 60000);

    // Mark offline on page unload
    const handleUnload = () => {
      navigator.sendBeacon(`${process.env.REACT_APP_API_URL || '/api'}/api/presence/offline`);
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(pollInterval);
      clearInterval(notificationPollInterval);
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      window.removeEventListener('beforeunload', handleUnload);
      // Clear typing timeouts
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
    };
  }, [isAuthenticated, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear notification after showing
  const dismissNotification = useCallback((index) => {
    setPendingNotifications(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Mark channel as read
  const markChannelRead = useCallback(async (channelId) => {
    // Update local state immediately
    setUnreadSummary(prev => ({
      ...prev,
      total_unread: Math.max(0, prev.total_unread - (prev.channels.find(c => c.channel_id === channelId)?.unread_count || 0)),
      channels: prev.channels.filter(c => c.channel_id !== channelId)
    }));
  }, []);

  const value = {
    myPresence,
    usersPresence,
    unreadSummary,
    emailUnread,
    typingUsers,
    connected,
    pendingNotifications,
    updateStatus,
    updateSettings,
    sendTypingIndicator,
    dismissNotification,
    markChannelRead,
    refreshUnread: loadUnreadSummary,
    refreshEmailUnread: loadEmailUnread,
    UserStatus
  };

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};

export const usePresence = () => {
  const context = useContext(PresenceContext);
  if (!context) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
};

export default PresenceContext;
